// Build the canonical "🔔 ..." source-list line for a reminder. Pure
// function — no Firestore, no React state. Used in two places:
//
//   1. BoardPage.updateReminderAndSource — to write the formatted line
//      back into the source list when the user edits a reminder.
//   2. ReminderEditPopover — to show a live read-only preview of what
//      the source line WILL look like as the user types in the
//      structured editor.
//
// Both paths must produce identical strings or the preview will lie.
// That's why this lives in a shared module instead of being duplicated.
//
// Canonical form is NL-first, colon-separated. The meta phrase is produced
// by buildResolvedDatePhrase (shared with the capture path), so these match
// exactly what typing the reminder writes:
//   Recurring + dated:  "🔔 every Tue at 8am: <task>"
//   Yearly (keeps M/D):  "🔔 every year 10/1 7am: <task>"
//   Recurring undated:  "🔔 every Tue: <task>"
//   One-off dated:      "🔔 5/12 8am: <task>"   (cross-year: "5/12/2027 8am")
//   No date, no recur:  "🔔 <task>"
//
// The ↻ "this repeats" glyph + the "starting 5/18" suffix were removed:
// "every Tue" already carries the recurrence semantic, and the first-
// occurrence date isn't actionable once the reminder is established —
// the agenda's "next instance" annotation covers forward-looking info.

import { parseReminderLine, formatRecurrenceLabel, normalizeRecurrence, buildResolvedDatePhrase } from './reminderParser';
import { hashReminderLine } from '../hooks/useReminders';
import { isOrganizeArtifactLine } from './organizeArtifacts';
import { parseExistingBellLine } from './parseExistingBellLine';
import { toAnchorDate } from './startAnchor';

// Done lines: Walter cycled through three forms here.
//   2026-05-11: "~~🔔 task~~"  — markdown strikethrough wrappers.
//                Fragile when content was edited near the line edges
//                and not keyboard-typeable.
//   2026-05-12: "🔕 task"      — bell-with-slash single glyph. Read
//                cleanly but Walter realised the muted bell isn't
//                discoverable from the keyboard.
//   2026-05-13: "[DONE] task"  — explicit text marker. Visually loud,
//                keyboard-typeable (user can mark done by editing the
//                prefix), and the bell-detection regex catches it the
//                same way it catches the legacy glyphs. Legacy forms
//                (🔕, ~~🔔) still parse as done for backwards compat
//                and migrate to [DONE] via stripRecurrenceGlyph.
const PREFIX = '🔔 ';
const DONE_PREFIX = '[DONE] ';

export function formatBellLine(reminder) {
  const rec = normalizeRecurrence(reminder.recurrence);
  const usePrefix = reminder.status === 'done' ? DONE_PREFIX : PREFIX;
  const rawText = reminder.text || '';

  // Defensive: strip a leading date phrase from text before composing.
  // Without this, text drift (e.g. an earlier writeback that left
  // "Sun 5/3 7am call liz" inside the text field) would duplicate as
  // "🔔 Mon 5/4 8am Sun 5/3 7am call liz". Parser sees a leading date
  // → restText is the clean tail. If parse doesn't see a date at the
  // very start, treat the whole text as clean.
  //
  // Walter 2026-05-21: the `dateIndex === 0` guard matters now that the
  // parser detects EMBEDDED (non-leading) dates (NL_INPUT_AUDIT §5e).
  // `reminder.text` is already the clean task text; re-parsing it must
  // only strip a genuine LEADING date, never a date word that happens
  // to sit mid-task ("the meeting on Friday"). BoardPage's equivalent
  // re-parse already carries this guard.
  const tp = parseReminderLine(rawText);
  const cleanText = tp.isReminder && tp.kind === 'dated' && tp.dateIndex === 0
    ? (tp.restText || '')
    : rawText;

  // Meta prefix — what goes between 🔔 and the colon. Empty when there's
  // neither a date nor a recurrence (a "🔔 <task>" plain reminder).
  //
  // Walter 2026-07-04: DELEGATE to buildResolvedDatePhrase — the single
  // source of truth for date + recurrence formatting. This block used to
  // carry its OWN copy of the date logic, which drifted from the capture
  // path (it still emitted the old space/apostrophe year, an extra "at",
  // and dropped a yearly rule's M/D anchor). Now the edit-writeback and the
  // live preview format IDENTICALLY to what capture writes.
  const d = reminder.parsedDate ? toDate(reminder.parsedDate) : null;
  const hasDate = d && !Number.isNaN(d.getTime());
  let meta = '';
  if (hasDate) {
    // hasCertainTime = true: the reminder doc's parsedDate already carries
    // the resolved time. Empty dateText → buildResolvedDatePhrase emits the
    // pure canonical phrase (numeric date, or recurrence label + anchor).
    // Walter 2026-09-26 (#246): a start date he typed survives an edit made
    // in the agenda popover. Without this the writeback rebuilt his line
    // without the "starting 10/2" clause - #245's fix undone by another path.
    meta = buildResolvedDatePhrase('', d, true, undefined, undefined, rec || null,
      (rec && toAnchorDate(reminder.startDate)) || false);
  } else if (rec) {
    // Recurrence with no (valid) fire date → label only, no end-condition.
    meta = formatRecurrenceLabel(rec, { omitEndCondition: true });
  }

  if (!meta) return (usePrefix + cleanText).trimEnd();
  return `${usePrefix}${meta}: ${cleanText}`.trimEnd();
}

function toDate(d) {
  if (!d) return null;
  if (d instanceof Date) return d;
  if (typeof d?.toDate === 'function') return d.toDate();
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? null : x;
}

/**
 * stripRecurrenceGlyph(content, listId, reminders)
 *
 * Walter 2026-05-12: cleans up two legacy bell-line forms on read:
 *   (a) "🔔↻" / "~~🔔↻" — the 2026-05-11 picker-round recurrence
 *       glyph, since reverted. The "every Tue" NL prefix now carries
 *       the recurrence semantic; the glyph was redundant + cryptic.
 *   (b) "~~🔔 task~~"   — the old strikethrough-wrapped done form.
 *       Now we use a single 🔕 glyph instead. Cleaner in plain text
 *       and avoids the fragile end-of-line "~~" wrapper that broke
 *       when users edited near the line boundary.
 *
 * Idempotent: a second pass over already-clean content is a no-op.
 * Callers should only persist when `next !== prev`. The signature
 * keeps the `listId` + `reminders` args of the previous
 * backfillRecurrenceGlyph so call sites don't need to change.
 */
/**
 * organizeReminderList(content, reminders) — one-shot "tidy this
 * reminder list" pass. Splits the content into bell-line blocks
 * (a bell line plus any attached continuation/note lines) and
 * groups them into sections by status + date:
 *
 *   # Overdue           — non-recurring, parsedDate < now, not done
 *   # Current           — non-recurring, parsedDate >= now (today + later)
 *   # Recurring         — has any recurrence rule, not done
 *   # Undated           — no parsedDate, not done
 *   # Done              — status === 'done'
 *   # Lines not processed — anything else (existing headers, notes
 *                          that weren't attached to a bell line)
 *                          gets a "// " prefix so the bell-line
 *                          scanner ignores them on next read.
 *
 * Walter 2026-05-13: triggered from a "↓ organize" button in the
 * reminder list's tile header and from the agenda panel toolbar.
 * One-shot; the toast offers undo back to the snapshot.
 *
 * Returns `{ newContent, changed }`. Callers only persist when
 * `changed` is true.
 */
export function organizeReminderList(content, reminders = []) {
  if (!content) return { newContent: content || '', changed: false };
  const BELL_DONE_RE = /^(?:\[DONE\]|🔕|~~🔔)↻?\s?/;
  const BELL_OPEN_RE = /^🔔↻?\s?/;
  // Walter 2026-05-14 (s3): strip every artifact from a previous
  // organize pass before re-classifying. Otherwise running organize
  // twice produces "// # CURRENT" / "// # DONE" comment-lines that
  // accumulate under "# Lines not processed" on every subsequent
  // pass. Walter 2026-08-24: the recogniser moved to lib/organizeArtifacts
  // (pure + testable) and now also catches bell-prefixed headers.
  const isOrganizeArtifact = isOrganizeArtifactLine;
  const cleanedLines = content.split('\n').filter(l => !isOrganizeArtifact(l));
  const lines = cleanedLines;
  const groups = {
    overdue: [],
    current: [],
    recurring: [],
    undated: [],
    done: [],
    other: [],
  };
  const now = new Date();
  let pending = null; // { kind, lines: [bellLine, ...continuations] }
  const remByHash = new Map();
  for (const r of reminders || []) {
    if (r && r.sourceLineHash) remByHash.set(r.sourceLineHash, r);
  }
  // Coerce any of Firestore Timestamp / Date / ISO-string / millis to
  // a real Date — used both for kind classification AND for sort.
  // Walter 2026-05-14 (#1): the previous `new Date(rem.parsedDate)`
  // pattern produced Invalid Date for Firestore Timestamps, which
  // pushed every reminder to Number.POSITIVE_INFINITY in the sort
  // comparator and left blocks in their original (unsorted) order.
  function asDate(v) {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v?.toDate === 'function') return v.toDate();
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  function flush() {
    if (!pending) return;
    groups[pending.kind].push(pending.lines);
    pending = null;
  }
  for (const line of lines) {
    if (line === '') {
      // Blank line ends the current block. We don't preserve blank
      // lines individually — section headers + per-block ordering
      // handle visual spacing.
      flush();
      continue;
    }
    const doneM = line.match(BELL_DONE_RE);
    const openM = !doneM ? line.match(BELL_OPEN_RE) : null;
    if (doneM || openM) {
      flush();
      const isDone = !!doneM;
      const prefix = (doneM || openM)[0];
      const post = line.slice(prefix.length);
      const rem = remByHash.get(hashReminderLine(post));
      let kind;
      if (isDone || rem?.status === 'done') {
        kind = 'done';
      } else if (rem?.recurrence) {
        kind = 'recurring';
      } else if (rem?.parsedDate) {
        const d = asDate(rem.parsedDate);
        kind = (d && d < now) ? 'overdue' : 'current';
      } else if (rem) {
        // Doc found, not done, no recurrence, no date → genuinely undated.
        kind = 'undated';
      } else {
        // Walter 2026-06-16: NO matching reminder doc for this bell line —
        // its hash drifted (the user edited the text after it was captured)
        // or capture hasn't synced yet. Don't blindly call it Undated:
        // PARSE THE LINE, which still carries its resolved date/recurrence,
        // so an edited dated reminder isn't mis-sorted into Undated. (Walter
        // hit this: edited "call tom" → "call tom again", sorted, and it
        // dropped into Undated even though the agenda still had it dated.)
        // Walter 2026-09-26: the line carries a bell, so read it as the
        // reminder it is - a trailing "?" used to land it in Undated.
        const parsed = parseExistingBellLine(post);
        if (parsed?.isReminder && parsed.recurrence) {
          kind = 'recurring';
        } else if (parsed?.isReminder && parsed.parsedDate) {
          const d = asDate(parsed.parsedDate);
          kind = (d && d < now) ? 'overdue' : 'current';
        } else {
          kind = 'undated';
        }
      }
      pending = { kind, lines: [line] };
    } else if (pending) {
      // Walter 2026-08-25: any non-blank, non-bell line under a reminder is
      // that reminder's note and travels with it when the list is sorted. The
      // blank-line check above already closed the block, so reaching here
      // means we're still inside the reminder's paragraph. (CONTINUATION_RE —
      // a leading space or bullet — is now just one way to write one, kept
      // because plenty of existing notes look like that.)
      pending.lines.push(line);
    } else {
      flush();
      groups.other.push([line]);
    }
  }
  flush();
  // Walter 2026-05-13: bail when there are zero bell-line blocks.
  // Without this, a list with only demo `//` comments or plain
  // headings rewrites to "# Lines not processed" + the same lines,
  // which destroys the list visually for no benefit. No bells = no
  // organising to do.
  const totalBells =
    groups.overdue.length +
    groups.current.length +
    groups.recurring.length +
    groups.undated.length +
    groups.done.length;
  if (totalBells === 0) return { newContent: content, changed: false };
  // Sort within sections by parsedDate ascending where applicable.
  // For "other" we preserve original order. Walter 2026-05-14 (#1):
  // uses asDate() so Firestore Timestamps coerce correctly — the
  // previous `new Date(timestamp)` produced Invalid Date and all
  // blocks tied at +Infinity, defeating the sort.
  function sortByDate(blocks, asc = true) {
    blocks.sort((a, b) => {
      const aHash = hashReminderLine(a[0].replace(BELL_OPEN_RE, '').replace(BELL_DONE_RE, ''));
      const bHash = hashReminderLine(b[0].replace(BELL_OPEN_RE, '').replace(BELL_DONE_RE, ''));
      const ar = remByHash.get(aHash);
      const br = remByHash.get(bHash);
      const ad = asDate(ar?.parsedDate);
      const bd = asDate(br?.parsedDate);
      const at = ad ? +ad : Number.POSITIVE_INFINITY;
      const bt = bd ? +bd : Number.POSITIVE_INFINITY;
      return asc ? at - bt : bt - at;
    });
  }
  sortByDate(groups.overdue, true);
  sortByDate(groups.current, true);
  sortByDate(groups.recurring, true);
  sortByDate(groups.done, false); // most recently done at the top of Done
  // Walter 2026-05-14 (#6a): section headers ALL CAPS for scannability;
  // blocks separated by a blank line for breathing room.
  const parts = [];
  function addSection(header, blocks) {
    if (!blocks.length) return;
    if (parts.length > 0) parts.push('');
    parts.push(`# ${header.toUpperCase()}`);
    parts.push('');
    blocks.forEach((block, i) => {
      if (i > 0) parts.push('');
      for (const ln of block) parts.push(ln);
    });
  }
  addSection('Overdue', groups.overdue);
  addSection('Current', groups.current);
  addSection('Recurring', groups.recurring);
  addSection('Undated', groups.undated);
  addSection('Done', groups.done);
  if (groups.other.length > 0) {
    // Walter 2026-05-19: section renamed from "LINES NOT PROCESSED" to
    // "DOESN'T LOOK LIKE A REMINDER" — friendlier wording, and the `//`
    // comment-prefix on each line is gone. Walter typed a line that
    // didn't parse, sorted, and thought the entry "disappeared
    // completely" because the `// ` prefix made it visually read as a
    // hidden comment in a section he scrolled past. Keep the lines at
    // full visual weight so they're obviously still there to review.
    // (Skipping the `// ` prefix means these lines might re-trigger
    // the reminder-capture flow if the user edits one; that's the
    // desired UX — they were SUPPOSED to be reminders.)
    if (parts.length > 0) parts.push('');
    parts.push("# DOESN'T LOOK LIKE A REMINDER");
    parts.push('');
    for (const block of groups.other) {
      for (const ln of block) {
        parts.push(ln);
      }
    }
  }
  const newContent = parts.join('\n');
  return { newContent, changed: newContent !== content };
}

export function stripRecurrenceGlyph(content, _listId, _reminders) {
  if (!content) return content;
  const needsWork =
    content.includes('↻') ||
    content.includes('~~🔔') ||
    content.includes('🔕');
  if (!needsWork) return content;
  let next = content;
  // (1) Legacy strikethrough form "~~🔔 task~~" → "[DONE] task".
  //     Match whole line; both leading ~~🔔 and trailing ~~ must be
  //     present. Optional ↻ between bell and body is consumed too.
  next = next.replace(/^~~🔔(↻?)\s?(.*?)~~\s*$/gm, (_m, _loop, body) => {
    return '[DONE] ' + body;
  });
  // (2) Round-1 muted-bell form "🔕 task" → "[DONE] task".
  next = next.replace(/^🔕↻?\s?/gm, '[DONE] ');
  // (3) Strip ↻ from any lingering 🔔↻ prefixes.
  next = next.replace(/^🔔↻\s?/gm, '🔔 ');
  return next;
}
